import React, { useCallback, useEffect, useState } from 'react';
import { inventoryApi } from '../api';

function formatCost(cost) {
  if (cost == null || cost === '') return '—';
  return `$${Number(cost).toFixed(2)}`;
}

const emptyAddForm = () => ({
  name: '',
  inventoryTypeId: '',
  quantity: 1,
  storageLocationId: '',
  description: '',
  cost: '',
  statusKey: 'inventory',
  locationId: '',
});

function AddItemModal({
  propertyId,
  types,
  storageLocations,
  locations,
  statuses,
  onClose,
  onSuccess,
}) {
  const [form, setForm] = useState(emptyAddForm);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const isInventory = form.statusKey === 'inventory';
  const isInstalled = form.statusKey === 'installed';

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await inventoryApi.createItem({
        propertyId: Number(propertyId),
        name: form.name.trim(),
        inventoryTypeId: Number(form.inventoryTypeId),
        quantity: Number(form.quantity),
        description: form.description.trim() || null,
        cost: form.cost !== '' ? Number(form.cost) : null,
        statusKey: form.statusKey,
        storageLocationId: isInventory ? Number(form.storageLocationId) : null,
        locationId: isInstalled ? Number(form.locationId) : null,
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add item');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
      <div className="modal-dialog">
        <form className="modal-content" onSubmit={submit}>
          <div className="modal-header">
            <h5 className="modal-title">Add inventory</h5>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
          </div>
          <div className="modal-body">
            {error && <div className="alert alert-danger py-2">{error}</div>}
            <div className="mb-3">
              <label className="form-label">Name *</label>
              <input
                className="form-control form-control-sm"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Light bulb"
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Type *</label>
              <select
                className="form-select form-select-sm"
                value={form.inventoryTypeId}
                onChange={(e) => setForm({ ...form, inventoryTypeId: e.target.value })}
                required
              >
                <option value="">Select type…</option>
                {types.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              {types.length === 0 && (
                <div className="form-text">No types for this property — add them in Inventory Admin.</div>
              )}
            </div>
            <div className="row g-2 mb-3">
              <div className="col-6">
                <label className="form-label">Quantity *</label>
                <input
                  type="number"
                  min={1}
                  className="form-control form-control-sm"
                  value={form.quantity}
                  onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                  required
                />
              </div>
              <div className="col-6">
                <label className="form-label">Unit cost</label>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  className="form-control form-control-sm"
                  value={form.cost}
                  onChange={(e) => setForm({ ...form, cost: e.target.value })}
                  placeholder="Optional"
                />
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">Status *</label>
              <select
                className="form-select form-select-sm"
                value={form.statusKey}
                onChange={(e) => setForm({
                  ...form,
                  statusKey: e.target.value,
                  storageLocationId: '',
                  locationId: '',
                })}
                required
              >
                {statuses.map((s) => (
                  <option key={s.id} value={s.key}>{s.name}</option>
                ))}
              </select>
            </div>
            {isInventory && (
              <div className="mb-3">
                <label className="form-label">Storage location *</label>
                <select
                  className="form-select form-select-sm"
                  value={form.storageLocationId}
                  onChange={(e) => setForm({ ...form, storageLocationId: e.target.value })}
                  required
                >
                  <option value="">Select storage location…</option>
                  {storageLocations.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                {storageLocations.length === 0 && (
                  <div className="form-text">No storage locations — add them in Inventory Admin.</div>
                )}
              </div>
            )}
            {isInstalled && (
              <div className="mb-3">
                <label className="form-label">Site location *</label>
                <select
                  className="form-select form-select-sm"
                  value={form.locationId}
                  onChange={(e) => setForm({ ...form, locationId: e.target.value })}
                  required
                >
                  <option value="">Select site location…</option>
                  {locations.map((loc) => (
                    <option key={loc.id} value={loc.id}>{loc.site_name}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="mb-0">
              <label className="form-label">Description</label>
              <textarea
                className="form-control form-control-sm"
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Optional"
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>
              {busy ? 'Saving…' : 'Add item'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SplitModal({ item, locations, onClose, onSuccess }) {
  const [quantity, setQuantity] = useState(1);
  const [locationId, setLocationId] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await inventoryApi.splitItem(item.id, {
        quantity: Number(quantity),
        locationId: Number(locationId),
        statusKey: 'installed',
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Split failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
      <div className="modal-dialog">
        <form className="modal-content" onSubmit={submit}>
          <div className="modal-header">
            <h5 className="modal-title">Install / allocate</h5>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
          </div>
          <div className="modal-body">
            <p className="small text-muted mb-3">
              Move quantity from <strong>{item.name}</strong> (available: {item.quantity}) to an installed site location.
            </p>
            {error && <div className="alert alert-danger py-2">{error}</div>}
            <div className="mb-3">
              <label className="form-label small">Quantity</label>
              <input
                type="number"
                className="form-control form-control-sm"
                min={1}
                max={item.quantity}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                required
              />
            </div>
            <div className="mb-0">
              <label className="form-label small">Site location *</label>
              <select
                className="form-select form-select-sm"
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
                required
              >
                <option value="">Select site location…</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.site_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>
              {busy ? 'Saving…' : 'Install'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function InventoryHome() {
  const [properties, setProperties] = useState([]);
  const [locations, setLocations] = useState([]);
  const [storageLocations, setStorageLocations] = useState([]);
  const [types, setTypes] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [items, setItems] = useState([]);

  const [propertyId, setPropertyId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [storageLocationId, setStorageLocationId] = useState('');
  const [typeId, setTypeId] = useState('');

  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [splitItem, setSplitItem] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    inventoryApi.properties()
      .then((res) => {
        setProperties(res.data);
        if (res.data.length === 1) setPropertyId(String(res.data[0].id));
      })
      .catch((e) => setError(e.response?.data?.error || 'Failed to load properties'));

    inventoryApi.statuses()
      .then((res) => setStatuses(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!propertyId) {
      setLocations([]);
      setStorageLocations([]);
      setTypes([]);
      return;
    }
    Promise.all([
      inventoryApi.locations(propertyId),
      inventoryApi.storageLocations(propertyId),
      inventoryApi.types(propertyId),
    ])
      .then(([locRes, storageRes, typeRes]) => {
        setLocations(locRes.data);
        setStorageLocations(storageRes.data);
        setTypes(typeRes.data);
      })
      .catch((e) => setError(e.response?.data?.error || 'Failed to load filters'));
  }, [propertyId]);

  const loadItems = useCallback(async () => {
    if (!propertyId) {
      setItems([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const params = { propertyId };
      if (locationId) params.locationId = locationId;
      if (storageLocationId) params.storageLocationId = storageLocationId;
      if (typeId) params.typeId = typeId;
      const res = await inventoryApi.items(params);
      setItems(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, [propertyId, locationId, storageLocationId, typeId]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const canSplit = (item) => item.status_key === 'inventory' && item.quantity >= 1;
  const isModalOpen = showAddModal || !!splitItem;

  return (
    <div className="app-shell-page">
      {error && <div className="alert alert-danger">{error}</div>}
      {msg && <div className="alert alert-success">{msg}</div>}

      <div className="card app-card bg-white mb-3">
        <div className="card-body">
          <div className="row g-2 align-items-end">
            <div className="col-md-3">
              <label className="form-label small mb-1">Property *</label>
              <select
                className="form-select form-select-sm"
                value={propertyId}
                onChange={(e) => {
                  setPropertyId(e.target.value);
                  setLocationId('');
                  setStorageLocationId('');
                  setTypeId('');
                }}
              >
                <option value="">Select property…</option>
                {properties.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label small mb-1">Installed at (site)</label>
              <select
                className="form-select form-select-sm"
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
                disabled={!propertyId}
              >
                <option value="">All site locations</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>{loc.site_name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label small mb-1">Storage location</label>
              <select
                className="form-select form-select-sm"
                value={storageLocationId}
                onChange={(e) => setStorageLocationId(e.target.value)}
                disabled={!propertyId}
              >
                <option value="">All storage locations</option>
                {storageLocations.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label small mb-1">Type</label>
              <select
                className="form-select form-select-sm"
                value={typeId}
                onChange={(e) => setTypeId(e.target.value)}
                disabled={!propertyId}
              >
                <option value="">All types</option>
                {types.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {!propertyId ? (
        <p className="text-muted">Select a property to view inventory.</p>
      ) : (
        <>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <h3 className="h5 mb-0">Inventory items</h3>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => {
                setMsg('');
                setShowAddModal(true);
              }}
            >
              Add inventory
            </button>
          </div>
          <div className="card app-card bg-white">
            <div className="card-body p-0">
              {loading ? (
                <p className="p-3 mb-0 text-muted">Loading…</p>
              ) : (
                <div className="app-table-wrap table-responsive">
                  <table className="table table-sm table-striped table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Name</th>
                        <th>Type</th>
                        <th>Qty</th>
                        <th>Status</th>
                        <th>Storage</th>
                        <th>Site</th>
                        <th>Cost</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {items.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="text-muted p-3 text-center">
                            No items match the current filters.
                          </td>
                        </tr>
                      ) : (
                        items.map((item) => (
                          <tr key={item.id}>
                            <td>{item.name}</td>
                            <td>{item.type_name}</td>
                            <td>{item.quantity}</td>
                            <td>{item.status_name}</td>
                            <td>{item.storage_location_name || '—'}</td>
                            <td>{item.location_name || '—'}</td>
                            <td>{formatCost(item.cost)}</td>
                            <td className="text-end">
                              {canSplit(item) && (
                                <button
                                  type="button"
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={() => setSplitItem(item)}
                                >
                                  Install / allocate
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {showAddModal && propertyId && (
        <AddItemModal
          propertyId={propertyId}
          types={types}
          storageLocations={storageLocations}
          locations={locations}
          statuses={statuses}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            setMsg('Item added.');
            loadItems();
          }}
        />
      )}

      {splitItem && (
        <SplitModal
          item={splitItem}
          locations={locations}
          onClose={() => setSplitItem(null)}
          onSuccess={() => {
            setSplitItem(null);
            setMsg('Item installed.');
            loadItems();
          }}
        />
      )}

      {isModalOpen && (
        <div
          className="modal-backdrop fade show"
          onClick={() => {
            setShowAddModal(false);
            setSplitItem(null);
          }}
          aria-hidden="true"
        />
      )}
    </div>
  );
}

export default InventoryHome;
